import { useState, useCallback, useRef, useEffect } from 'react'
import {
  fetchYardiCompanies,
  fetchYardiProperties,
  fetchPropertyDetail,
  fetchCompanyDetail,
  postYardiManualInvite,
  getApplicationStatus,
} from '../services/yardiInviteService'
import { validateData } from '../utils/yardiInviteFormatters'
import {
  yardiInviteAddSchema,
  firstAddFormErrorMessage,
  focusFirstInvalidYardiAddField,
} from '../utils/yardiInviteValidators'
import { buildYardiInviteAddResetState } from '../utils/yardiInviteAddReset'
import { showError, formatApiError } from '@/lib/utils/notifications'
import { htmlOrTextToPlain } from '@/lib/utils/htmlPlainText'
import {
  companyNotActiveWithYardiMessage,
  propertyNotActiveWithYardiMessage,
  showValidationModalsSequential,
} from '../utils/yardiInviteNotifications'
import {
  COMPANY_NOT_ACTIVE_LIBERTY_MESSAGE,
  PROPERTY_NOT_ACTIVE_LIBERTY_MESSAGE,
} from '../constants'

/** Plain-text error message for the Result table from HTML or JSON error payloads. */
function formatManualInviteError(error: unknown): string {
  const ax = error as { response?: { data?: unknown } }
  const data = ax?.response?.data

  if (typeof data === 'string') {
    return htmlOrTextToPlain(data)
  }

  if (data && typeof data === 'object' && data !== null && 'error' in data) {
    const errVal = (data as { error: unknown }).error
    if (typeof errVal === 'string') {
      return htmlOrTextToPlain(errVal)
    }
    if (errVal !== null && typeof errVal === 'object' && 'error' in (errVal as object)) {
      const inner = (errVal as { error: unknown }).error
      if (typeof inner === 'string') return htmlOrTextToPlain(inner)
    }
    if (errVal !== undefined) {
      return htmlOrTextToPlain(typeof errVal === 'string' ? errVal : JSON.stringify(errVal))
    }
  }

  // Strip HTML to plain for the inline Result cell
  return htmlOrTextToPlain(formatApiError(error))
}

export function useYardiInviteAdd(roleOpts: {
  propertyId: string | null
  companyId: string | null
  propertyName: string | null
  companyName: string | null
}) {
  const [companyId, setCompanyId] = useState('')
  const [propertyId, setPropertyId] = useState('')
  const [prospectId, setProspectId] = useState('')
  const [companyOptions, setCompanyOptions] = useState<{ value: string; label: string }[]>([])
  const [propertyOptions, setPropertyOptions] = useState<{ value: string; label: string }[]>([])
  /** Busy for dropdown/permission fetches; independent of invite submit locking. */
  const [dropdownBusy, setDropdownBusy] = useState(false)
  /** True from successful POST until status poll reaches a terminal state (or POST error / reset). */
  const [inviteBlocking, setInviteBlocking] = useState(false)
  const [initialPageLoading, setInitialPageLoading] = useState(true)
  const [readOnlyControl, setReadOnlyControl] = useState<string[]>([])
  const [validateApplicationForm, setValidateApplicationForm] = useState(false)
  const [result, setResult] = useState<{ status: string; reason: string }>({ status: '', reason: '' })
  const pollingRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const debounceCompanyRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const debouncePropertyRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const companyRequestIdRef = useRef(0)
  const propertyRequestIdRef = useRef(0)
  const companyInFlightKeyRef = useRef<string | null>(null)
  const propertyInFlightKeyRef = useRef<string | null>(null)
  const companyIdRef = useRef(companyId)
  companyIdRef.current = companyId
  /** Cached company labels so the selected id stays readable if search omits it. */
  const allCompanyLabelByIdRef = useRef<Record<string, string>>({})
  /** Role-locked defaults, restored on Reset. */
  const roleDefaultCompanyRef = useRef<{ id: string; name: string } | null>(null)
  const roleDefaultPropertyRef = useRef<{ id: string; name: string } | null>(null)

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearTimeout(pollingRef.current)
      pollingRef.current = null
    }
  }, [])

  const loadProperties = useCallback(
    async (q: string, compId: string) => {
      const key = `${compId || ''}\0${q}`
      if (propertyInFlightKeyRef.current === key) return
      propertyInFlightKeyRef.current = key
      const requestId = ++propertyRequestIdRef.current
      setDropdownBusy(true)
      try {
        const rows = await fetchYardiProperties(q, compId || undefined)
        if (requestId !== propertyRequestIdRef.current) return
        const list = rows.map((p) => ({
          value: String(p.property_id),
          label: p.property_name || String(p.property_id),
        }))
        setPropertyOptions(list)
        if (list.length === 1) {
          window.setTimeout(() => {
            if (requestId !== propertyRequestIdRef.current) return
            setPropertyId(list[0].value)
          }, 10)
        }
      } catch (e) {
        if (requestId === propertyRequestIdRef.current) {
          showError(formatApiError(e), false)
        }
      } finally {
        if (propertyInFlightKeyRef.current === key) {
          propertyInFlightKeyRef.current = null
        }
        if (requestId === propertyRequestIdRef.current) {
          setDropdownBusy(false)
        }
      }
    },
    [],
  )

  const loadCompanies = useCallback(
    async (q: string) => {
      const key = q
      if (companyInFlightKeyRef.current === key) return
      companyInFlightKeyRef.current = key
      const requestId = ++companyRequestIdRef.current
      setDropdownBusy(true)
      try {
        const rows = await fetchYardiCompanies(q)
        if (requestId !== companyRequestIdRef.current) return
        const list = rows.map((c) => {
          const id = String(c.company_id)
          const label = c.company_name || id
          allCompanyLabelByIdRef.current[id] = label
          return { value: id, label }
        })
        setCompanyOptions((prev) => {
          const base = list
          const v = String(companyIdRef.current)
          if (!v || base.some((o) => String(o.value) === v)) return base
          const label =
            allCompanyLabelByIdRef.current[v] ??
            prev.find((o) => String(o.value) === v)?.label ??
            v
          return [{ value: v, label }, ...base]
        })
        /** Single company → select it and reload its properties. */
        if (list.length === 1) {
          const v = list[0].value
          window.setTimeout(() => {
            if (requestId !== companyRequestIdRef.current) return
            setCompanyId(v)
            if (v && !roleOpts.propertyId) {
              setPropertyId('')
              setPropertyOptions([])
              void loadProperties('', v)
            }
          }, 10)
        }
      } catch (e) {
        if (requestId === companyRequestIdRef.current) {
          showError(formatApiError(e), false)
        }
      } finally {
        if (companyInFlightKeyRef.current === key) {
          companyInFlightKeyRef.current = null
        }
        if (requestId === companyRequestIdRef.current) {
          setDropdownBusy(false)
        }
      }
    },
    [roleOpts.propertyId, loadProperties],
  )

  const searchCompany = useCallback(
    (text: string) => {
      if (debounceCompanyRef.current) clearTimeout(debounceCompanyRef.current)
      debounceCompanyRef.current = setTimeout(() => loadCompanies(text), 300)
    },
    [loadCompanies],
  )

  const searchProperty = useCallback(
    (text: string) => {
      if (debouncePropertyRef.current) clearTimeout(debouncePropertyRef.current)
      debouncePropertyRef.current = setTimeout(() => loadProperties(text, companyId), 300)
    },
    [loadProperties, companyId],
  )

  const companySelected = useCallback(
    (cid: string) => {
      if (cid && !roleOpts.propertyId) {
        setPropertyId('')
        setPropertyOptions([])
        loadProperties('', cid)
      }
    },
    [roleOpts.propertyId, loadProperties],
  )

  const getAccessiblePermission = useCallback(async () => {
    const validationMessages: string[] = []

    if (roleOpts.propertyId) {
      setDropdownBusy(true)
      try {
        const propertydata = await fetchPropertyDetail(roleOpts.propertyId)
        if (propertydata?.yardi_property_id === null) {
          validationMessages.push(
            propertyNotActiveWithYardiMessage(propertydata.property_name ?? ''),
          )
          setReadOnlyControl(['property', 'company'])
        } else if (propertydata?.listed === '1') {
          setReadOnlyControl((r) => [...new Set([...r, 'property'])])
          const pid = String(roleOpts.propertyId!)
          const plabel = roleOpts.propertyName || pid
          roleDefaultPropertyRef.current = { id: pid, name: plabel }
          setPropertyOptions([{ value: pid, label: plabel }])
          setPropertyId(pid)
        } else {
          validationMessages.push(PROPERTY_NOT_ACTIVE_LIBERTY_MESSAGE)
          setReadOnlyControl(['property', 'company'])
        }
      } catch (e) {
        showError(formatApiError(e), false)
      } finally {
        setDropdownBusy(false)
      }
    }
    if (roleOpts.companyId) {
      setDropdownBusy(true)
      try {
        const companydata = await fetchCompanyDetail(roleOpts.companyId)
        if (companydata?.yardi_pmc_id === null) {
          validationMessages.push(
            companyNotActiveWithYardiMessage(companydata.company_name ?? ''),
          )
          setReadOnlyControl(['company', 'property'])
        } else if (companydata?.listed === '1') {
          setReadOnlyControl((r) => [...new Set([...r, 'company'])])
          const lockedCid = String(roleOpts.companyId)
          const lockedLabel = roleOpts.companyName || lockedCid
          roleDefaultCompanyRef.current = { id: lockedCid, name: lockedLabel }
          allCompanyLabelByIdRef.current[lockedCid] = lockedLabel
          setCompanyOptions([{ value: lockedCid, label: lockedLabel }])
          setCompanyId(roleOpts.companyId)
        } else {
          validationMessages.push(COMPANY_NOT_ACTIVE_LIBERTY_MESSAGE)
          setReadOnlyControl(['company', 'property'])
        }
      } catch (e) {
        showError(formatApiError(e), false)
      } finally {
        setDropdownBusy(false)
      }
    }

    if (validationMessages.length > 0) {
      showValidationModalsSequential(validationMessages)
    }
  }, [roleOpts.propertyId, roleOpts.companyId, roleOpts.propertyName, roleOpts.companyName])

  // Run permission checks before the company list load so role locks aren't overwritten.
  useEffect(() => {
    void (async () => {
      try {
        await getAccessiblePermission()
        await loadCompanies('')
      } finally {
        setInitialPageLoading(false)
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => () => stopPolling(), [stopPolling])

  const getFilterValue = useCallback(() => {
    const raw = {
      property_id: propertyId,
      company_id: companyId,
      prospect_id: prospectId,
    }
    const filterParams: Record<string, string> = {}
    for (const k of Object.keys(raw) as (keyof typeof raw)[]) {
      let v = raw[k]
      v = typeof v === 'string' ? v.trim() : v
      if (v) filterParams[k] = v
    }
    return filterParams
  }, [propertyId, companyId, prospectId])

  const applicationStatusPolling = useCallback((applicationId: number, interval: number) => {
    getApplicationStatus(applicationId)
      .then((res) => {
        if (res.state === 'INQUEUE') {
          pollingRef.current = setTimeout(
            () => applicationStatusPolling(applicationId, interval),
            interval,
          )
        } else if (res.state === 'INVITE_SENT') {
          setInviteBlocking(false)
          setResult({
            status: 'Invite Sent',
            reason: `Invite sent with invite code ${res.invite_code ?? ''}`,
          })
        } else {
          setInviteBlocking(false)
          setResult({
            status: 'Failed',
            reason: htmlOrTextToPlain(String(res.lr_check_error ?? '')),
          })
        }
      })
      .catch((error: unknown) => {
        setInviteBlocking(false)
        setResult({ status: 'Failed', reason: formatManualInviteError(error) })
      })
  }, [])

  const submitForm = useCallback(async () => {
    setValidateApplicationForm(true)
    const parsed = yardiInviteAddSchema.safeParse({ company_id: companyId, property_id: propertyId, prospect_id: prospectId })
    if (!parsed.success) {
      showError(firstAddFormErrorMessage(parsed.error), false, undefined, 'Validation Message')
      focusFirstInvalidYardiAddField(parsed.error)
      return
    }
    setInviteBlocking(true)
    setResult({ status: 'Processing', reason: '' })
    try {
      const body = validateData({ ...getFilterValue() }) as Record<string, unknown>
      const applicationId = await postYardiManualInvite(body)
      applicationStatusPolling(applicationId, 3000)
    } catch (error: unknown) {
      setInviteBlocking(false)
      const reason = formatManualInviteError(error)
      setResult({ status: 'Failed', reason })
    }
  }, [companyId, propertyId, prospectId, getFilterValue, applicationStatusPolling])

  const resetForm = useCallback(() => {
    stopPolling()
    setInviteBlocking(false)
    setValidateApplicationForm(false)
    setProspectId('')
    setResult({ status: '', reason: '' })

    const roleCompany = roleDefaultCompanyRef.current
    const roleProperty = roleDefaultPropertyRef.current
    const next = buildYardiInviteAddResetState({
      roleDefaultCompanyId: roleCompany?.id ?? null,
      roleDefaultCompanyName: roleCompany?.name ?? null,
      roleDefaultPropertyId: roleProperty?.id ?? null,
      roleDefaultPropertyName: roleProperty?.name ?? null,
    })

    if (next.companyId) {
      allCompanyLabelByIdRef.current[next.companyId] =
        roleCompany?.name ?? next.companyOptions[0]?.label ?? next.companyId
    }
    setCompanyId(next.companyId)
    setPropertyId(next.propertyId)
    setCompanyOptions(next.companyOptions)
    setPropertyOptions(next.propertyOptions)
  }, [stopPolling])

  /** Show invalid state on focus out. */
  const onFocusOutValidate = useCallback(() => {
    setValidateApplicationForm(true)
  }, [])

  /** POST + poll while status row still shows `Processing`. */
  const inviteSubmitInFlight = inviteBlocking && result.status === 'Processing'
  /** Invite pipeline or dropdown fetch in progress. */
  const processing = inviteBlocking || dropdownBusy

  return {
    companyId,
    setCompanyId,
    propertyId,
    setPropertyId,
    prospectId,
    setProspectId,
    companyOptions,
    propertyOptions,
    processing,
    inviteBlocking,
    initialPageLoading,
    inviteSubmitInFlight,
    readOnlyControl,
    validateApplicationForm,
    result,
    searchCompany,
    searchProperty,
    companySelected,
    submitForm,
    resetForm,
    onFocusOutValidate,
    loadCompanies,
    loadProperties,
  }
}
